// Déclaration d'accessibilité (RGAA 4.1) — lot F du 07/09/2026, question Q7 du dossier
// « produit fini » tranchée par défaut : statut « partiellement conforme », audit rapide,
// corrections évidentes. Le modèle est celui de la DINUM ; le contenu est HONNÊTE et ne
// prétend rien qui n'ait été vérifié : pas de taux de conformité tant que l'audit RGAA
// complet (106 critères, échantillon de pages, lecteurs d'écran) n'a pas été mené.
// Rapport de mesure : docs/audit/2026-09-07-audit-mobile-accessibilite.md.
// Page mi-légale : le DOMAINE déclaré et l'adresse de contact restent en dur (c'est le
// site réellement servi et la boîte réellement relevée — les écrire faux rendrait la
// déclaration inexacte au sens du RGAA), mais la MARQUE passe par `NOM_PRODUIT` depuis le
// 13/09 : « TerObs s'engage » nomme le service, pas la partie au contrat. Le nom de la
// société, lui, vit dans les mentions légales et attend l'immatriculation.
import { Link } from 'react-router-dom'
import AppLayout from './AppLayout'
import './obs/obs.css'
import { usePageMeta } from './hooks/usePageMeta'
import { DOMAINE_PUBLIC, EMAIL_CONTACT, NOM_PRODUIT } from './constants'

const DATE_DECLARATION = '7 septembre 2026'

export default function AccessibilitePage() {
  usePageMeta({
    title: `Déclaration d'accessibilité — ${NOM_PRODUIT}`,
    description: `Déclaration d'accessibilité RGAA 4.1 de ${DOMAINE_PUBLIC} : état de conformité, ` +
      'contenus non accessibles, méthode de vérification, contact et voies de recours.',
  })
  return (
    <AppLayout>
      <div className="obs2-jetons text-(--c-text) max-w-3xl mx-auto px-4 py-12">
        <h1 className="obs2-h1">Déclaration d'accessibilité</h1>
        <p className="text-sm text-(--c-muted) mb-10">
          Établie le {DATE_DECLARATION} selon le modèle du référentiel général d'amélioration de
          l'accessibilité (RGAA), version 4.1.
        </p>

        <section className="obs2-card mb-6">
          <p className="text-sm text-(--c-text) leading-relaxed">
            {NOM_PRODUIT} s'engage à rendre son service en ligne accessible, conformément à
            l'article 47 de la loi n° 2005-102 du 11 février 2005.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            Cette déclaration d'accessibilité s'applique au site <strong>{DOMAINE_PUBLIC}</strong> :
            la vitrine, l'observatoire des communes (vue d'ensemble et pages
            thématiques), les pages d'information et les pages légales.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            Elle est publiée volontairement : l'éditeur n'entre pas, à ce jour, dans le champ
            des organismes tenus de la publier. Ses clients — communes, intercommunalités,
            départements — y sont soumis, et l'outil qu'ils mettent à disposition de leurs
            équipes et de leurs administrés doit pouvoir s'inscrire dans cette obligation.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">État de conformité</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            {DOMAINE_PUBLIC} est <strong>partiellement conforme</strong> avec le référentiel général
            d'amélioration de l'accessibilité (RGAA), version 4.1, en raison des non-conformités
            et des dérogations énumérées ci-dessous.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Résultats des tests</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            Cette déclaration repose sur un <strong>audit rapide automatisé et une revue manuelle
            partielle du {DATE_DECLARATION}</strong>, réalisés par l'éditeur, complétés par un
            premier passage exploratoire du 6 août 2026. <strong>Un audit RGAA complet — la grille
            des 106 critères appliquée à un échantillon représentatif de pages, avec lecteurs
            d'écran — n'a pas été réalisé.</strong> Aucun taux de conformité n'est donc publié :
            le statut « partiellement conforme » traduit l'état constaté sur les pages vérifiées,
            et sera remplacé par le résultat de l'audit complet dès qu'il aura été mené.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            Ce qui a été vérifié, sur téléphone (390 × 844) et sur bureau (1 280 × 900) :
            la langue et le titre des pages, l'unicité et la hiérarchie des titres, les
            alternatives des images, le nom accessible des liens et boutons, l'étiquetage des
            champs, la structure des tableaux, le contraste des textes tels qu'ils sont
            affichés (WCAG 2.1, 4,5:1), la visibilité du focus au clavier, la taille des cibles
            tactiles, l'absence de défilement horizontal et la lisibilité des tailles de police.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            Pages vérifiées : l'accueil, la vue d'ensemble d'une commune en accès public
            (Palaiseau), la thématique Démographie de cette commune, la page « Collectivités :
            comment s'abonner » et les mentions légales.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Contenus non accessibles</h2>
          <h3 className="font-semibold text-(--c-text) mb-2">Non-conformités</h3>
          <ul className="list-disc pl-5 space-y-2 text-sm text-(--c-text) leading-relaxed">
            <li>
              Les graphiques sont dessinés dans un élément <code>canvas</code>. Chaque graphique
              porte un intitulé et ses données sont proposées dans un tableau repliable « Voir
              les données du graphique » ; la tendance qu'il illustre n'est pas décrite en
              toutes lettres (critère 1.3, partiellement couvert).
            </li>
            <li>
              Certaines informations sont portées par la seule couleur — pastilles des
              thématiques et séries des graphiques (critère 3.1). Le tableau de données et
              les étiquettes de valeur écrites sur les graphiques en sont l'alternative.
            </li>
            <li>
              Les fenêtres modales (demande de devis, fiches techniques, export de l'étude)
              se ferment au clic hors de la fenêtre et, pour certaines, à la touche Échap,
              mais ne retiennent pas le focus clavier et ne le restituent pas à la fermeture
              (critères 7.1 et 12.x).
            </li>
            <li>
              Lors d'une navigation dans l'application sans rechargement de page, le titre
              est mis à jour mais le focus n'est pas déplacé vers le nouveau contenu
              (critère 12.x).
            </li>
            <li>
              Les cartes interactives (MapLibre GL) ne sont pas utilisables au clavier ni par un
              lecteur d'écran ; les valeurs qu'elles représentent sont disponibles sous forme
              de tuiles et de tableaux.
            </li>
            <li>
              Quelques commandes secondaires restent en dessous de 44 px de côté (curseurs
              et sélecteurs des graphiques) — au-dessus du minimum de 24 px.
            </li>
            <li>
              Aucun test avec un lecteur d'écran (NVDA, JAWS, VoiceOver) n'a été mené : des
              défauts de restitution peuvent subsister sans avoir été repérés.
            </li>
          </ul>
          <h3 className="font-semibold text-(--c-text) mt-6 mb-2">Dérogations pour charge disproportionnée</h3>
          <ul className="list-disc pl-5 space-y-2 text-sm text-(--c-text) leading-relaxed">
            <li>
              Les études au format PDF (jusqu'à une centaine de pages, générées à la demande
              pour les abonnés) ne sont pas structurées selon la norme PDF/UA. Les mêmes
              données sont consultables en ligne et exportables au format tableur (Excel, CSV).
            </li>
          </ul>
          <h3 className="font-semibold text-(--c-text) mt-6 mb-2">Contenus non soumis à l'obligation d'accessibilité</h3>
          <p className="text-sm text-(--c-text) leading-relaxed">Aucun.</p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Établissement de cette déclaration</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex flex-col sm:flex-row">
              <dt className="sm:w-48 shrink-0 text-(--c-muted)">Date</dt>
              <dd className="font-medium text-(--c-text)">{DATE_DECLARATION}</dd>
            </div>
            <div className="flex flex-col sm:flex-row">
              <dt className="sm:w-48 shrink-0 text-(--c-muted)">Technologies</dt>
              <dd className="font-medium text-(--c-text)">HTML5, CSS, JavaScript (React), SVG, Canvas</dd>
            </div>
            <div className="flex flex-col sm:flex-row">
              <dt className="sm:w-48 shrink-0 text-(--c-muted)">Environnement de test</dt>
              <dd className="font-medium text-(--c-text)">
                Chromium 148 (Brave) en émulation téléphone (390 × 844) et bureau
                (1 280 × 900), navigation au clavier. Pas de test avec lecteur d'écran.
              </dd>
            </div>
            <div className="flex flex-col sm:flex-row">
              <dt className="sm:w-48 shrink-0 text-(--c-muted)">Outils</dt>
              <dd className="font-medium text-(--c-text)">
                Script d'audit de l'éditeur, versionné avec le site (contrastes WCAG 2.1 des
                textes rendus, structure, alternatives, focus, cibles tactiles, lisibilité
                mobile) ; revue manuelle du code.
              </dd>
            </div>
          </dl>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Retour d'information et contact</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            Si vous ne parvenez pas à accéder à un contenu ou à un service, vous pouvez nous
            écrire pour être orienté vers une alternative accessible ou obtenir le contenu
            sous une autre forme :{' '}
            <a href={`mailto:${EMAIL_CONTACT}?subject=Accessibilit%C3%A9`} className="text-(--c-brand) hover:underline font-medium">
              {EMAIL_CONTACT}
            </a>{' '}
            (indiquez « accessibilité » dans l'objet). Vous pouvez aussi passer par la{' '}
            <Link to="/contact" className="text-(--c-brand) hover:underline font-medium">page de contact</Link>.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Voies de recours</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            Cette procédure est à utiliser dans le cas suivant : vous avez signalé au
            responsable du site un défaut d'accessibilité qui vous empêche d'accéder à un
            contenu ou à l'un des services, et vous n'avez pas obtenu de réponse
            satisfaisante.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-sm text-(--c-text) leading-relaxed mt-3">
            <li>
              Écrire un message au Défenseur des droits :{' '}
              <a href="https://formulaire.defenseurdesdroits.fr/" className="text-(--c-brand) hover:underline font-medium" rel="noopener noreferrer">
                formulaire.defenseurdesdroits.fr
              </a>
            </li>
            <li>
              Contacter le délégué du Défenseur des droits dans votre région :{' '}
              <a href="https://www.defenseurdesdroits.fr/carte-des-delegues" className="text-(--c-brand) hover:underline font-medium" rel="noopener noreferrer">
                defenseurdesdroits.fr/carte-des-delegues
              </a>
            </li>
            <li>
              Envoyer un courrier par la poste (gratuit, ne pas mettre de timbre) :
              Défenseur des droits, Libre réponse 71120, 75342 Paris CEDEX 07
            </li>
            <li>Téléphoner au 09 69 39 00 00 (coût d'un appel local)</li>
          </ul>
        </section>

        <p className="text-xs text-(--c-muted) mt-8">
          Déclaration mise à jour le {DATE_DECLARATION}. Elle sera révisée à l'issue de
          l'audit RGAA complet, et à chaque évolution notable du site.{' '}
          <Link to="/mentions-legales" className="underline hover:text-(--c-brand)">Mentions légales</Link>.
        </p>
      </div>
    </AppLayout>
  )
}
